import { useState } from "react";
import { syncBootstrap, syncFixtures, fullSync, rebuildScores, wcSyncBootstrap, wcSyncFixtures, wcFullSync } from "../services/adminService";
import { getErrorMessage, apiPost } from "../services/api";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import AlertMessage from "../components/AlertMessage";

const FPL_ACTIONS = [
  {
    id: "fpl-bootstrap",
    title: "Sync Bootstrap",
    description: "Pull the latest teams and gameweeks from the FPL API.",
    fn: syncBootstrap,
    color: "bg-sky-600 hover:bg-sky-700",
  },
  {
    id: "fpl-fixtures",
    title: "Sync Fixtures",
    description: "Pull the latest fixture list and scores, and re-score any finished fixtures.",
    fn: syncFixtures,
    color: "bg-amber-600 hover:bg-amber-700",
  },
  {
    id: "fpl-all",
    title: "Full Sync",
    description: "Run bootstrap sync followed by fixture sync in one go.",
    fn: fullSync,
    color: "bg-emerald-600 hover:bg-emerald-700",
  },
  {
    id: "scoring",
    title: "Rebuild Scores",
    description: "Recalculate all prediction scores based on current actual fixture results.",
    fn: rebuildScores,
    color: "bg-purple-600 hover:bg-purple-700",
  },
];

const WC_ACTIONS = [
  {
    id: "wc-bootstrap",
    title: "WC Sync Bootstrap",
    description: "Pull the latest national teams and gameweeks from API-Football.",
    fn: (options) => wcSyncBootstrap(2026, options),
    color: "bg-orange-600 hover:bg-orange-700",
  },
  {
    id: "wc-fixtures",
    title: "WC Sync Fixtures",
    description: "Pull the latest World Cup fixture list and scores, and re-score any finished fixtures.",
    fn: (options) => wcSyncFixtures(2026, options),
    color: "bg-red-600 hover:bg-red-700",
  },
  {
    id: "wc-all",
    title: "WC Full Sync",
    description: "Run WC bootstrap sync followed by fixture sync in one go.",
    fn: (options) => wcFullSync(2026, options),
    color: "bg-rose-600 hover:bg-rose-700",
  },
];

function AdminPage() {
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});
  const [selectedGameweekId, setSelectedGameweekId] = useState("");

  async function handleRun(action) {
    if (running) return;

    setRunning(action.id);
    setResults((prev) => ({ ...prev, [action.id]: null }));

    const controller = new AbortController();

    try {
      const data = await action.fn({ signal: controller.signal });
      setResults((prev) => ({
        ...prev,
        [action.id]: { ok: true, message: data?.message || "Completed successfully." },
      }));
    } catch (error) {
      setResults((prev) => ({
        ...prev,
        [action.id]: { ok: false, message: getErrorMessage(error) },
      }));
    } finally {
      setRunning(null);
    }
  }

  async function handleSendReminders() {
    if (!selectedGameweekId) {
      alert("Please select a gameweek");
      return;
    }

    if (running) return;

    setRunning("send-reminders");
    setResults((prev) => ({ ...prev, "send-reminders": null }));

    try {
      const data = await apiPost("/admin/send-pick-reminders", {
        gameweekId: parseInt(selectedGameweekId),
      });

      setResults((prev) => ({
        ...prev,
        "send-reminders": {
          ok: true,
          message: data?.message || "Reminders sent successfully.",
          data: data,
        },
      }));
    } catch (error) {
      setResults((prev) => ({
        ...prev,
        "send-reminders": { ok: false, message: getErrorMessage(error) },
      }));
    } finally {
      setRunning(null);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Admin Panel"
        subtitle="Manually trigger data syncs for FPL and World Cup. These actions call protected API endpoints."
        dark
      />

      <SectionCard
        title="Pick Reminders"
        subtitle="Manually trigger pick reminder emails for a gameweek."
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-900 mb-2">
              Select Gameweek
            </label>
            <input
              type="number"
              value={selectedGameweekId}
              onChange={(e) => setSelectedGameweekId(e.target.value)}
              placeholder="Enter gameweek ID"
              className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
            <p className="mt-1 text-xs text-slate-500">
              Enter the gameweek ID to send reminders for users with incomplete picks.
            </p>
          </div>

          <button
            onClick={handleSendReminders}
            disabled={!!running || !selectedGameweekId}
            className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {running === "send-reminders" ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Sending Reminders...
              </span>
            ) : (
              "Send Pick Reminders"
            )}
          </button>

          {results["send-reminders"] && (
            <div className="space-y-3">
              <AlertMessage
                message={results["send-reminders"].message}
                type={results["send-reminders"].ok ? "success" : "error"}
              />
              {results["send-reminders"].data && (
                <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Gameweek</p>
                      <p className="font-semibold text-slate-900">{results["send-reminders"].data.gameweekName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Users</p>
                      <p className="font-semibold text-slate-900">{results["send-reminders"].data.totalUsersWithIncompletePicks}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Sent</p>
                      <p className="font-semibold text-emerald-600">{results["send-reminders"].data.emailsSent}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Failed</p>
                      <p className="font-semibold text-red-600">{results["send-reminders"].data.emailsFailed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Skipped</p>
                      <p className="font-semibold text-amber-600">{results["send-reminders"].data.emailsSkipped}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="FPL Sync"
        subtitle="Trigger individual FPL sync steps or run the full sync pipeline."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FPL_ACTIONS.map((action) => {
            const result = results[action.id];
            const isRunning = running === action.id;
            const isDisabled = !!running;

            return (
              <div
                key={action.id}
                className="flex flex-col rounded-2xl bg-slate-50 p-4 sm:p-5 ring-1 ring-slate-200"
              >
                <h4 className="text-sm font-semibold text-slate-900">{action.title}</h4>
                <p className="mt-1 flex-1 text-xs sm:text-sm text-slate-500">{action.description}</p>

                <button
                  onClick={() => handleRun(action)}
                  disabled={isDisabled}
                  className={`mt-4 inline-flex w-full items-center justify-center rounded-2xl px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${action.color}`}
                >
                  {isRunning ? (
                    <span className="flex items-center gap-2">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                      </svg>
                      Running...
                    </span>
                  ) : (
                    `Run ${action.title}`
                  )}
                </button>

                {result && (
                  <div className="mt-3">
                    <AlertMessage
                      message={result.message}
                      type={result.ok ? "success" : "error"}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard
        title="World Cup Sync"
        subtitle="Trigger individual World Cup sync steps or run the full sync pipeline for season 2026."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WC_ACTIONS.map((action) => {
            const result = results[action.id];
            const isRunning = running === action.id;
            const isDisabled = !!running;

            return (
              <div
                key={action.id}
                className="flex flex-col rounded-2xl bg-slate-50 p-4 sm:p-5 ring-1 ring-slate-200"
              >
                <h4 className="text-sm font-semibold text-slate-900">{action.title}</h4>
                <p className="mt-1 flex-1 text-xs sm:text-sm text-slate-500">{action.description}</p>

                <button
                  onClick={() => handleRun(action)}
                  disabled={isDisabled}
                  className={`mt-4 inline-flex w-full items-center justify-center rounded-2xl px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${action.color}`}
                >
                  {isRunning ? (
                    <span className="flex items-center gap-2">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                      </svg>
                      Running...
                    </span>
                  ) : (
                    `Run ${action.title}`
                  )}
                </button>

                {result && (
                  <div className="mt-3">
                    <AlertMessage
                      message={result.message}
                      type={result.ok ? "success" : "error"}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

export default AdminPage;
